import type { WorkflowStepInstanceOutput } from "@/types";

type PropertyValue = string | number | undefined;

export interface OutputOption {
  key: string;
  type: string;
  value: string;
  selected: boolean;
}

export interface SelectOutputValue {
  context: string;
  options: OutputOption[];
}

/**
 * Gets the minimum and maximum selection constraints for an output
 */
export function getOptionConstraints(output: WorkflowStepInstanceOutput): {
  min?: number;
  max?: number;
} {
  const props = output.workflowStepOutputDefinition?.properties ?? [];
  const rawMin = props.find((p) => p.key === "MinSelection")?.value as PropertyValue;
  const rawMax = props.find((p) => p.key === "MaxSelection")?.value as PropertyValue;

  const toNum = (val: string | number | undefined): number | undefined => {
    if (val === undefined || val === null) return undefined;
    const n = typeof val === "string" ? Number.parseInt(val, 10) : val;
    return Number.isNaN(n) ? undefined : n;
  };

  return { min: toNum(rawMin), max: toNum(rawMax) };
}

/**
 * Validates if the current selections meet the constraints
 */
export function isSubmitValid(
  outputs: WorkflowStepInstanceOutput[],
  responseOptionsList: OutputOption[][],
  submittedFeedback: Set<string>,
  approvedOutputs: Set<string>,
  selectionMode: "single" | "multiple" | null,
  parsedOutputs: Array<{
    output: WorkflowStepInstanceOutput;
    parsed: SelectOutputValue;
  }>,
): boolean {
  return outputs.every((output) => {
    const hasFeedback = submittedFeedback.has(output.id) || output.hasFeedback;
    const isApproved = approvedOutputs.has(output.id);

    if (hasFeedback || isApproved) {
      return true;
    }

    const { min, max } = getOptionConstraints(output);
    const selectedCount =
      responseOptionsList[
        parsedOutputs.findIndex(({ output: o }) => o.id === output.id)
      ]?.filter((o) => o.selected).length || 0;

    if (selectionMode === "multiple") {
      const meetsMin = min === undefined || selectedCount >= min;
      const meetsMax = max === undefined || selectedCount <= max;
      return meetsMin && meetsMax;
    } else if (selectionMode === "single") {
      return selectedCount === 1;
    }

    return false;
  });
}

/**
 * Parses output values into structured format
 */
export function parseOutputs(outputs: WorkflowStepInstanceOutput[]): Array<{
  output: WorkflowStepInstanceOutput;
  parsed: SelectOutputValue;
}> {
  return outputs.map((output) => {
    let parsed: SelectOutputValue = { context: "", options: [] };

    if (typeof output.outputValue === "string") {
      try {
        parsed = JSON.parse(output.outputValue) as SelectOutputValue;
      } catch (error) {
        console.error("Failed to parse output value:", error);
      }
    }

    return { output, parsed };
  });
}
